import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Notification } from '@/types';
import { notificationsApi } from '@/services/api';

interface NotificationsState {
  items: Notification[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    hasMore: boolean;
  };
}

const initialState: NotificationsState = {
  items: [],
  unreadCount: 0,
  isLoading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    hasMore: false,
  },
};

export const fetchNotifications = createAsyncThunk(
  'notifications/fetchNotifications',
  async (params?: { page?: number; limit?: number }, { rejectWithValue }) => {
    try {
      const response = await notificationsApi.list(params);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Erro ao carregar notificações');
      }
      return { data: response.data, pagination: response.pagination };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Erro ao carregar notificações');
    }
  }
);

export const markAsRead = createAsyncThunk(
  'notifications/markAsRead',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await notificationsApi.markAsRead(id);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Erro ao marcar como lida');
      }
      return id;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Erro ao marcar como lida');
    }
  }
);

export const markAllAsRead = createAsyncThunk(
  'notifications/markAllAsRead',
  async (_, { rejectWithValue }) => {
    try {
      const response = await notificationsApi.markAllAsRead();
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Erro ao marcar todas como lidas');
      }
      return null;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Erro ao marcar todas como lidas');
    }
  }
);

export const fetchUnreadCount = createAsyncThunk(
  'notifications/fetchUnreadCount',
  async (_, { rejectWithValue }) => {
    try {
      const response = await notificationsApi.getUnreadCount();
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Erro ao carregar contador');
      }
      return response.data.count;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Erro ao carregar contador');
    }
  }
);

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    addNotification: (state, action: PayloadAction<Notification>) => {
      state.items.unshift(action.payload);
      if (!action.payload.read) {
        state.unreadCount += 1;
      }
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      const index = state.items.findIndex(item => item.id === action.payload);
      if (index !== -1) {
        const notification = state.items[index];
        if (!notification.read) {
          state.unreadCount -= 1;
        }
        state.items.splice(index, 1);
      }
    },
    clearNotifications: (state) => {
      state.items = [];
      state.unreadCount = 0;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotifications.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.isLoading = false;
        const { data, pagination } = action.payload;
        
        if (pagination?.page === 1) {
          state.items = data;
        } else {
          state.items.push(...data);
        }
        
        if (pagination) {
          state.pagination = {
            page: pagination.page,
            limit: pagination.perPage,
            total: pagination.total,
            hasMore: pagination.hasNext,
          };
        }
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(markAsRead.fulfilled, (state, action) => {
        const notification = state.items.find(item => item.id === action.payload);
        if (notification && !notification.read) {
          notification.read = true;
          notification.readAt = new Date().toISOString();
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      })
      .addCase(markAllAsRead.fulfilled, (state) => {
        state.items.forEach(item => {
          if (!item.read) {
            item.read = true;
            item.readAt = new Date().toISOString();
          }
        });
        state.unreadCount = 0;
      })
      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        state.unreadCount = action.payload;
      });
  },
});

export const {
  addNotification,
  removeNotification,
  clearNotifications,
  clearError,
} = notificationsSlice.actions;

export default notificationsSlice.reducer;